import { ForwardedRef, forwardRef, Fragment, useMemo } from 'react';
import {
  Controller,
  ControllerFieldState,
  ControllerRenderProps,
  FieldPath,
  FieldValues,
  useFormContext,
} from 'react-hook-form';

import { useMergeRefs } from '@xemplo/ref-utils';

import { usePhoneFieldRhf } from './hooks/use-phone-field-rhf';
import PhoneField from './phone-field';
import { PhoneFieldRhfProps } from './phone-field.types';

type FieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  field: ControllerRenderProps<TFieldValues, TName>;
  fieldState: ControllerFieldState;
};

/**
 * PhoneFieldRhf is a React component that integrates the PhoneField with React Hook Form.
 * It uses the Controller component to manage the form state and validation.
 *
 * @param props - The properties for the PhoneFieldRhf component.
 * @returns A PhoneField component wrapped with React Hook Form's Controller.
 */
export const PhoneFieldRhf = forwardRef(
  (props: PhoneFieldRhfProps, ref?: ForwardedRef<HTMLInputElement>) => {
    const { name, rules } = props;
    const formContext = useFormContext();
    const renderField = useMemo(() => fieldFactory(props, ref), [props, ref]);
    return (
      <Controller
        name={name}
        control={formContext.control}
        rules={rules}
        shouldUnregister={rules?.shouldUnregister}
        render={renderField}
      />
    );
  },
);

/**
 * In order to be able to use the `useMergeRefs`, had to extract the component render.
 * This should ensure correctness of the of the react-hook-form integration.
 **/
const fieldFactory = (
  props: PhoneFieldRhfProps,
  ref?: ForwardedRef<HTMLInputElement>,
) => {
  const { rules, onBlur, ...inputProps } = props;
  return ({ field, fieldState }: FieldProps) => {
    const {
      isReady,
      defaultCountryCode,
      displayValue,
      phoneFieldRef,
      handleBlur,
      handleChange,
      handleValueChange,
      handleCountryChange,
    } = usePhoneFieldRhf(field, props);
    const mergedRefs = useMergeRefs([field.ref, ref, phoneFieldRef]);

    // If I simply return null here, React Hook Form throws a type error.
    // eslint-disable-next-line react/jsx-no-useless-fragment
    if (!isReady) return <Fragment />;

    return (
      <PhoneField
        {...inputProps}
        required={!!rules?.required}
        ref={mergedRefs}
        value={displayValue}
        defaultCountryCode={defaultCountryCode}
        onBlur={handleBlur}
        onChange={handleChange}
        onValueChange={handleValueChange}
        onCountryChange={handleCountryChange}
        errorMessage={fieldState.error?.message?.toString()}
      />
    );
  };
};
